import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { CONTENT_API_VERSION } from '@glass/common/modules/content/contentTypes';
import capitalizeFirstLetter from '@glass/common/modules/utils/capitalizeFirstLetter';
import List from '@glass/web/components/base/List';
import Paper from '@glass/web/components/base/Paper';
import Skeleton from '@glass/web/components/base/Skeleton';
import Typography from '@glass/web/components/base/Typography';
import useLoadingResources from '@glass/web/modules/resources/useLoadingResources';
import { CENTER } from '@glass/web/modules/theme/constants';
import { FLEX, SPACE_BETWEEN } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import ContentListItem from '@glass/shared/components/Section/JobTitleContentExamplesSection/ContentListItem';
import contentTextsQuery from '@glass/shared/modules/content/staticVariations/queries/contentTexts.graphql';
import usePageTerms from '@glass/shared/modules/content/staticVariations/usePageTerms';
import { WEEKLY_SEED } from '@glass/shared/modules/random/seeds';
import { RANDOM_ORDER_BY } from '@glass/shared/modules/resources/orderBys';
import resourceTitles from '@glass/shared/modules/resources/resourceTitles';
import { selectUserContents } from '@glass/shared/modules/userContent';
import toggleUserContentAction from '@glass/shared/modules/userContent/toggleUserContentAction';

const useStyles = makeStyles()((theme) => ({
  list: {
    overflow: 'auto',
    position: 'relative',
    maxHeight: '75vh',
  },
  title: {
    padding: theme.spacing(1.5, 1.5, 0),
    display: FLEX,
    flexWrap: 'wrap',
    alignItems: CENTER,
    justifyContent: SPACE_BETWEEN,
  },
  paper: {
    height: '100%',
  },
}));

const NUM_CONTENT_RESULTS = 10;

const SLUG_VARIABLES = {
  orderBy: RANDOM_ORDER_BY,
  first: NUM_CONTENT_RESULTS,
};

const ALL_OPTIONS = {
  ssr: false,
};

function ContentList({ section, title: titleProp }) {
  const { classes } = useStyles();
  const { jSlug, iSlug } = usePageTerms();
  const dispatch = useDispatch();

  const contentOptions = useMemo(() => {
    const type = capitalizeFirstLetter(section);
    if (jSlug) {
      return {
        ...ALL_OPTIONS,
        variables: {
          ...SLUG_VARIABLES,
          seed: `${WEEKLY_SEED}${jSlug}`,
          where: { jobTitles_some: { slug: jSlug }, type },
          version: CONTENT_API_VERSION,
        },
      };
    }
    if (iSlug) {
      return {
        ...ALL_OPTIONS,
        variables: {
          type,
          ...SLUG_VARIABLES,
          seed: `${WEEKLY_SEED}${iSlug}`,
          where: { jobTitles_some: { industries_some: { slug: iSlug } }, type },
          version: CONTENT_API_VERSION,
        },
      };
    }
    return {
      ...ALL_OPTIONS,
      variables: {
        where: {
          type,
        },
        version: CONTENT_API_VERSION,
      },
      skip: true,
    };
  }, [iSlug, jSlug, section]);

  const { data, loading } = useQuery(contentTextsQuery, contentOptions);

  const userContents = useSelector(selectUserContents, shallowEqual);

  const handleContentToggle = useCallback(
    (contentProps) => dispatch(toggleUserContentAction(contentProps)),
    [dispatch],
  );

  const title = useMemo(
    () => titleProp || `${resourceTitles[section].title}`,
    [section, titleProp],
  );

  const loadingResources = useLoadingResources(NUM_CONTENT_RESULTS, { key: 'id' });

  const resources =
    (loading && !data?.contentTexts?.length ? loadingResources : data?.contentTexts) || [];

  if (!resources?.length) {
    return null;
  }

  return (
    <Paper className={classes.paper}>
      <Typography className={classes.title} component="h3" variant="h4">
        {title}
      </Typography>
      <List className={classes.list}>
        {data?.contentTexts?.map(({ id, text, width }) => (
          <ContentListItem
            key={id}
            favorite={!!userContents[section]?.[id]?.favorite || false}
            id={id}
            loading={loading}
            section={section}
            text={
              loading ? (
                <span>
                  <Skeleton width="100%" />
                  <Skeleton width="100%" />
                  <Skeleton width={width} />
                </span>
              ) : (
                text
              )
            }
            onContentToggle={handleContentToggle}
          />
        ))}
      </List>
    </Paper>
  );
}

ContentList.propTypes = {
  section: PropTypes.string,
  title: PropTypes.string,
};

export default React.memo(ContentList);
